import type { ButtonHTMLAttributes } from "react";

import "./tones.css";
import "./icon-button.css";
import "./focus.css";
import {
  iconButtonClass,
  type IconButtonSize,
  type IconButtonTone,
  type IconButtonVariant,
} from "./icon-button-class";

export type { IconButtonSize, IconButtonTone, IconButtonVariant } from "./icon-button-class";

// `aria-label` is required: an icon-only button has no text, so it must carry an accessible
// name. Omitting it from the base props and re-adding it as required makes that a typecheck
// error, not a runtime a11y bug.
export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "aria-label"
> {
  variant?: IconButtonVariant;
  tone?: IconButtonTone;
  size?: IconButtonSize;
  "aria-label": string;
}

/**
 * Square, icon-only button. Token-driven via the variant×tone×size grammar; one focus-visible
 * ring, a tap target that is the element's own box (≥24px, WCAG AA), reduced-motion aware. Pass the icon as
 * `children`. Defaults to a neutral ghost.
 */
export function IconButton({
  variant = "ghost",
  tone = "neutral",
  size = "md",
  className,
  type = "button",
  ...rest
}: IconButtonProps) {
  return (
    <button type={type} className={iconButtonClass(variant, tone, size, className)} {...rest} />
  );
}
